import * as Sentry from '@sentry/react';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { getApiErrorText, isKnownErrorCode, isKnownWarningCode } from 'locales/features/apiErrors';
import { toast } from 'react-toastify';
import { isStrongAuthenticationError } from 'types/api/strong_authentication';
import type { ErrorDescription } from 'types/handled_error';
import { app } from 'utils/appEnv';
import { stringUtils } from 'utils/stringUtils';
import { isHandled } from './handledErrors';

export type ApiError = {
  error: string;
  status: string;
  // FKS-1601 Allow for multiple errors in the response
  sub_errors: unknown;
  error_type?: string;
  error_code?: string;
  error_codes?: string[];
  error_message?: string;
  error_details?: Record<string, unknown>;
};

const reportStaleAppStateToSentry = (
  errorDescription: ErrorDescription,
  url: string,
  errorDetails?: Record<string, string>
) => {
  Sentry.captureMessage('stale_app_state', {
    level: 'warning',
    contexts: {
      api: {
        url: url,
        method: errorDescription.method,
        status: errorDescription.responseStatus,
        details: errorDetails,
      },
    },
  });
};

class Api {
  baseURL: string;

  client: AxiosInstance;

  /**
   * constructor
   * Create an instance of the faksApi class.
   *
   * @param {string} baseURL
   */
  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.client = axios.create({
      baseURL,
    });

    this.client.interceptors.response.use(
      (response) => {
        if (
          response.data?.meta === undefined ||
          response.data?.meta.errors === undefined ||
          response.data?.auth_token !== undefined
        ) {
          return app.env === 'development'
            ? JSON.parse(
                JSON.stringify(response).replace(
                  /http:\/\/localhost:3000\//gi,
                  baseURL.replace('/v1', '')
                )
              )
            : response;
        } else {
          const { status } = response;

          if (status !== 200) {
            const message = Object.keys(response.data.meta.errors)
              .map((field) => `${field}: ${response.data.meta.errors[field][0]}`)
              .join('\n');

            toast.error(message);
          }

          return response;
        }
      },
      (error) => {
        if (error.code === 'ERR_NETWORK') {
          toast.error('Oups ! Veuillez vérifier votre connexion puis réessayer.');
        } else {
          const errorBody = error?.response?.data;
          if (
            typeof errorBody === 'string' &&
            errorBody.startsWith('ActiveRecord::PendingMigrationError')
          ) {
            toast.error('You need to run your migrations.');
            return Promise.reject(error);
          }

          const errorDescription: ErrorDescription = {
            endpoint: this.getEndpoint(error?.config?.url),
            method: error?.config?.method,
            responseStatus: error?.response?.status,
            error: error?.response?.data?.error,
          };
          const errorCode = error?.response?.data?.error_code;
          const errorDetails: Record<string, string> | undefined =
            error?.response?.data?.error_details;

          if (errorCode === 'stale_app_state') {
            reportStaleAppStateToSentry(errorDescription, error.request.responseURL, errorDetails);
          }

          if (isStrongAuthenticationError(error?.response?.data)) {
            return Promise.reject(error);
          }

          if (!isHandled(errorDescription)) {
            if (isKnownWarningCode(errorCode)) {
              toast.warning(getApiErrorText(errorCode, errorDetails));
            } else if (isKnownErrorCode(errorCode)) {
              toast.error(getApiErrorText(errorCode, errorDetails));
            } else {
              toast.error(
                getApiErrorText('unknown_api_error', {
                  responseStatus: errorDescription.responseStatus?.toString(),
                  errorCode,
                })
              );
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  get = async <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
    const result = await this.client.get<T>(path, config);
    return result.data;
  };

  post = async <T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    const result = await this.client.post<T>(path, data, config);
    return result.data;
  };

  put = async <T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    const result = await this.client.put<T>(path, data, config);
    return result.data;
  };

  patch = async <T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    const result = await this.client.patch<T>(path, data, config);
    return result.data;
  };

  delete = async <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
    const result = await this.client.delete<T>(path, config);
    return result.data;
  };

  getEndpoint = (endpoint: string): string => {
    const parsed_endpoint = endpoint?.split('v2/').pop() || endpoint;
    const path = parsed_endpoint.split('/');
    if (stringUtils.isStringNumerical(path.pop())) return path.join('/');
    return parsed_endpoint;
  };
}

export const faksApiInstance = new Api(import.meta.env.REACT_APP_API_URL || '');
